import type { KeyboardEvent, MouseEvent as ReactMouseEvent, RefObject } from 'react';

import { useAppTranslations } from '@treneo/shared/i18n';
import { Spacing } from '@treneo/shared/themes';
import { Box, Button, BUTTON_VARIANTS, type ButtonVariant, styled } from '@treneo/shared/ui';

import { SubmitButton } from './submit-button';

const ButtonsContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: ${Spacing.medium};
  justify-content: flex-end;
  align-items: center;
`;

type FormButtonsProps = {
  onSubmit: () => void;
  onCancel?: () => void;
  submitViaClickOnly?: boolean;
  submitText?: string;
  cancelText?: string;
  isLoading?: boolean;
  loadingText?: string;
  isDisabled?: boolean;
  showCancel?: boolean;
  submitVariant?: ButtonVariant;
  cancelVariant?: ButtonVariant;
  submitButtonRef?: RefObject<HTMLButtonElement | null>;
  onSubmitKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
};

export const FormButtons = ({
  onSubmit,
  onCancel,
  submitViaClickOnly = false,
  submitText,
  cancelText,
  isLoading = false,
  loadingText,
  isDisabled = false,
  showCancel = true,
  submitVariant = BUTTON_VARIANTS.contained,
  cancelVariant = BUTTON_VARIANTS.outlined,
  submitButtonRef,
  onSubmitKeyDown
}: Readonly<FormButtonsProps>) => {
  const { t } = useAppTranslations();

  const defaultSubmitText = t('Save');
  const defaultCancelText = t('Cancel');

  return (
    <ButtonsContainer>
      {showCancel && (
        <Button variant={cancelVariant} onClick={onCancel ?? (() => {})} disabled={isLoading}>
          {cancelText ?? defaultCancelText}
        </Button>
      )}
      <SubmitButton
        variant={submitVariant}
        type='submit'
        {...(submitViaClickOnly && {
          onClick: (e: ReactMouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onSubmit();
          }
        })}
        onKeyDown={onSubmitKeyDown}
        isLoading={isLoading}
        loadingText={loadingText}
        disabled={isDisabled}
        ref={submitButtonRef}
      >
        {submitText ?? defaultSubmitText}
      </SubmitButton>
    </ButtonsContainer>
  );
};
